'use client';

import { useMemo, useCallback, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Lock } from 'lucide-react';

import { useAppNavigation } from '../hooks/useAppNavigation';
import { usePrefetch } from '../hooks/usePrefetch';
import { useSidebar } from '../providers/SidebarProvider';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setCurrentView, selectCurrentView } from '../store/slices/messagingUiSlice';
import { useSecureAuthContext } from '../auth/services';
import UserAvatar from '@/core/components/common/UserAvatar';
import {
  IconInbox,
  IconMessageHeart,
  IconMessageSquare,
  IconContact,
  IconTeams,
  IconHelp,
  IconNotification,
  IconSettings,
  IconCollapse,
  IconLogout
} from '@/shared/icons';
import type { ConversationViewType } from '@/api/features/conversations';
import { useMessagingEnabled } from '../../features/onboarding/hooks/useMessagingEnabled';
import { useGetOrCreateOnboardingSessionQuery } from '@/api/features/onboarding';
import { useGetOrganizationDetailsQuery } from '@/api/features/organizations';
import { Skeleton } from '@/shared/components/skeletons/Skeleton';
import PendingApprovalButton from '@/shared/components/common/PendingApprovalButton';

interface NavItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  path?: string;
  viewType?: ConversationViewType;
  section: 'main' | 'bottom';
  prefetchPriority?: 'high' | 'low';
  requiresMessaging?: boolean;  // Whether this item requires messaging to be enabled
}

/**
 * Refactored Sidebar Component
 * 
 * Key improvements:
 * - Uses unified navigation hook
 * - Smart prefetching with proper debouncing
 * - No direct state management for navigation
 * - Cleaner separation of concerns
 * - Optimistic UI updates
 */
export default function Sidebar() {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { navigate } = useAppNavigation();
  const { prefetchOnHover, cancelHoverPrefetch, prefetch } = usePrefetch();
  const { logout } = useSecureAuthContext();
  const { isMessagingEnabled } = useMessagingEnabled();

  // Check onboarding session for pending approval status
  const { data: onboardingSession } = useGetOrCreateOnboardingSessionQuery(undefined, {
    refetchOnFocus: false,
    refetchOnMountOrArgChange: false,
  });
  const currentOnboardingStep = (onboardingSession as { currentStep?: string } | undefined)?.currentStep;
  const showPendingApproval = currentOnboardingStep === 'awaiting_setup';

  const userProfile = useAppSelector(state => state.userProfile.session);
  const displayName = userProfile?.fullName || userProfile?.email || 'User';
  
  // Fetch organization data for company name and brand color
  const organizationId = userProfile?.organizationId;
  const { data: organizationData, isLoading: isLoadingOrg } = useGetOrganizationDetailsQuery(organizationId ?? '', {
    skip: !organizationId
  });
  const companyName = organizationData?.name || '';
  const brandColor = organizationData?.brandColor || '#22577A';
  
  // Drive active state from Redux so the highlight updates the instant the user
  // clicks — before the URL or server-component re-render completes.
  const currentView = useAppSelector(selectCurrentView);
  
  // Navigation items configuration
  const navItems: NavItem[] = useMemo(() => [
    // Conversation views
    {
      id: 'inbox-all',
      icon: <IconInbox size={22} />,
      label: 'All Conversations',
      viewType: 'all',
      section: 'main',
      prefetchPriority: 'high',
    },
    {
      id: 'inbox-assigned',
      icon: <IconMessageHeart size={22} />,
      label: 'Assigned to Me',
      viewType: 'assigned',
      section: 'main',
    },
    {
      id: 'inbox-unassigned',
      icon: <IconMessageSquare size={22} />,
      label: 'Unassigned',
      viewType: 'unassigned',
      section: 'main',
    },
    // Main pages
    {
      id: 'contacts',
      icon: <IconContact size={22} />,
      label: 'Contacts',
      path: '/contacts',
      section: 'main',
      prefetchPriority: 'high',
    },
    {
      id: 'teams',
      icon: <IconTeams size={22} />,
      label: 'Teams',
      path: '/teams',
      section: 'main',
      prefetchPriority: 'high',
      // Team chat is internal communication and should always be available
    },
    // Bottom section
    {
      id: 'help',
      icon: <IconHelp size={22} />,
      label: 'Help',
      path: '/help',
      section: 'bottom',
    },
    {
      id: 'notifications',
      icon: <IconNotification size={22} />,
      label: 'Notifications',
      path: '/notifications',
      section: 'bottom',
    },
    {
      id: 'settings',
      icon: <IconSettings size={22} />,
      label: 'Settings',
      path: '/settings',
      section: 'bottom',
      prefetchPriority: 'low',
    },
  ], []);

  // Eagerly prefetch primary routes once per session after initial mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('eager_prefetch_done') === '1') return;

    const pathsToPrefetch: string[] = [
      '/contacts',
      '/teams',
      '/settings',
      '/notifications',
      '/help',
      '/dashboard?view=assigned',
      '/dashboard?view=unassigned',
    ];

    const runPrefetch = () => {
      // Stagger prefetches slightly to avoid burst
      pathsToPrefetch.forEach((path, index) => {
        setTimeout(() => prefetch(path), index * 150);
      });
      sessionStorage.setItem('eager_prefetch_done', '1');
    };

    // Use requestIdleCallback when available for minimal main-thread impact
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number; cancelIdleCallback?: (id: number) => void; }).requestIdleCallback;
    let idleId: number | null = null;

    if (typeof idle === 'function') {
      idleId = idle(runPrefetch, { timeout: 1500 });
      return () => {
        const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void; }).cancelIdleCallback;
        if (idleId !== null && typeof cancel === 'function') cancel(idleId);
      };
    } else {
      const timeoutId = setTimeout(runPrefetch, 800);
      return () => clearTimeout(timeoutId);
    }
  }, [prefetch]);
  
  // Check if nav item is active
  const isItemActive = useCallback((item: NavItem): boolean => {
    if (item.viewType) {
      return (
        pathname === '/dashboard' ||
        pathname === '/'
      ) && currentView === item.viewType;
    }
    return pathname === item.path;
  }, [pathname, currentView]);
  
  // Handle navigation click
  const handleNavClick = useCallback((item: NavItem) => {
    if (item.viewType) {
      // No-op if already on this view — prevents dropping the conversation param
      // and avoids triggering an unnecessary server round-trip.
      const isAlreadyHere = (pathname === '/dashboard' || pathname === '/') && currentView === item.viewType;
      if (isAlreadyHere) return;

      // Dispatch synchronously BEFORE navigate so reduxCurrentView is correct
      // on the very next render — eliminating the one-render lag that caused
      // wrong conversations to appear when switching views.
      dispatch(setCurrentView(item.viewType));

      // Preserve the conversation param so the chat panel doesn't flash and
      // useConversationAutoSelection doesn't need to fire a second router.push.
      const params = new URLSearchParams(searchParams);
      params.set('view', item.viewType);

      // { immediate: true } bypasses startTransition so the navigation fires
      // right away — no deferral that could make the switch feel sluggish.
      navigate(`/dashboard?${params.toString()}`, { immediate: true });
    } else if (item.path) {
      navigate(item.path);
    }
  }, [navigate, dispatch, currentView, pathname, searchParams]);
  
  // Handle hover prefetch
  const handleNavHover = useCallback((item: NavItem) => {
    const path = item.path || (item.viewType ? `/dashboard?view=${item.viewType}` : null);
    if (path && !isItemActive(item)) {
      prefetchOnHover(path);
    }
  }, [prefetchOnHover, isItemActive]);
  
  // Handle hover leave
  const handleNavLeave = useCallback((item: NavItem) => {
    const path = item.path || (item.viewType ? `/dashboard?view=${item.viewType}` : null);
    if (path) {
      cancelHoverPrefetch(path);
    }
  }, [cancelHoverPrefetch]);
  
  // Filter items by section
  const mainItems = navItems.filter(item => item.section === 'main');
  const bottomItems = navItems.filter(item => item.section === 'bottom');
  
  return (
    <aside 
      className={`
        sidebar-container
        ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}
      `}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="sidebar-content">
        {/* Header */}
        <header className="sidebar-header">
          <div className={`sidebar-logo ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}>
            {!isCollapsed && (
              <div className="sidebar-logo-centered">
                <div className="sidebar-brand-wrapper">
                  {isLoadingOrg ? (
                    <>
                      <Skeleton className="w-8 h-8 rounded-md flex-shrink-0" />
                      <Skeleton className="h-4 w-24" />
                    </>
                  ) : (
                    <>
                      <div
                        className="sidebar-brand-icon"
                        style={{ backgroundColor: brandColor }}
                      >
                        <span className="sidebar-brand-icon-letter">
                          {companyName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="sidebar-company-name">
                        {companyName}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <button 
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isCollapsed}
          >
            <IconCollapse size={16} isCollapsed={isCollapsed} />
          </button>
        </header>

        {/* Main Navigation */}
        <nav className="sidebar-nav-main">
          {mainItems.map(item => (
            <NavButton
              key={item.id}
              item={item}
              isActive={isItemActive(item)}
              isCollapsed={isCollapsed}
              onClick={() => handleNavClick(item)}
              onMouseEnter={() => handleNavHover(item)}
              onMouseLeave={() => handleNavLeave(item)}
              showLock={item.requiresMessaging && !isMessagingEnabled}
            />
          ))}

          {/* Pending Approval Button - shown during awaiting_setup */}
          {showPendingApproval && (
            <PendingApprovalButton isCollapsed={isCollapsed} />
          )}
        </nav>
        
        {/* Bottom Navigation */}
        <div className="sidebar-nav-bottom">
          {bottomItems.map(item => (
            <NavButton
              key={item.id}
              item={item}
              isActive={isItemActive(item)}
              isCollapsed={isCollapsed}
              onClick={() => handleNavClick(item)}
              onMouseEnter={() => handleNavHover(item)}
              onMouseLeave={() => handleNavLeave(item)}
            />
          ))}
          
          <div className="sidebar-divider" />
          
          {/* User Profile */}
          <div className="sidebar-user">
            <div className="sidebar-user-content">
              <UserAvatar
                size={isCollapsed ? "large" : "large"}
                name={displayName}
                profileImageUrl={userProfile?.profileImageUrl}
                avatarColor={userProfile?.avatarColor}
                className={isCollapsed ? "sidebar-avatar-collapsed" : ""}
              />
              {!isCollapsed && (
                <div className="sidebar-user-info">
                  <p className="sidebar-user-name">{displayName}</p>
                  <p className="sidebar-user-email">{userProfile?.email}</p>
                </div>
              )}
              <button 
                className="sidebar-logout-btn"
                aria-label="Log out"
                onClick={logout}
              >
                <IconLogout size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

// Optimized NavButton component
interface NavButtonProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  disabled?: boolean;
  showLock?: boolean;
}

function NavButton({ 
  item,
  isActive,
  isCollapsed,
  onClick,
  onMouseEnter,
  onMouseLeave,
  showLock = false,
}: NavButtonProps) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-tour-id={item.id}
      className={`
        sidebar-nav-btn no-hover-transform
        ${isActive ? 'sidebar-nav-btn-active' : ''}
        ${isCollapsed ? 'sidebar-nav-btn-collapsed' : ''}
        ${showLock ? 'opacity-60' : ''}
      `}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      title={isCollapsed ? (showLock ? `${item.label} (Locked)` : item.label) : undefined}
    >
      <span className="sidebar-nav-icon">{item.icon}</span>
      {!isCollapsed && (
        <span className="sidebar-nav-label flex items-center gap-2">
          {item.label}
          {showLock && <Lock size={14} className="text-gray-400" />}
        </span>
      )}
      {isCollapsed && showLock && (
        <Lock size={12} className="absolute top-1 right-1 text-gray-400" />
      )}
    </button>
  );
}